import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { supabase } from "../services/supabase";

export type Gender = "male" | "female";

export interface User {
  id: string;
  email: string;
  name: string;
  phoneNumber: string;
  gender: Gender;
  emailVerified: boolean;
  accountType: string;
  superadmin: boolean;
  favorites?: string[];
  searchHistory?: string[];
}

interface UserContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  setUser: (user: User) => void;
  clearUser: () => void;
  logout: () => Promise<void>;
}

const UserContext = createContext<UserContextType>({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  setUser: () => {},
  clearUser: () => {},
  logout: async () => {},
});

export const useUser = () => useContext(UserContext);

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider = ({ children }: UserProviderProps) => {
  const [user, setUserState] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const setUser = (newUser: User) => {
    setUserState(newUser);
  };

  const clearUser = () => {
    setUserState(null);
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setUserState(null);
  };

  useEffect(() => {
    // Check initial session
    const initializeAuth = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (session) {
        const u = session.user;
        const metadata = u.user_metadata;
        const provider = u.app_metadata?.provider || "email";
        const accountType = provider === "google" ? "google" : provider === "apple" ? "apple" : "email";

        setUserState({
          id: u.id,
          email: u.email || "",
          name: metadata?.full_name || metadata?.name || "",
          phoneNumber: metadata?.phoneNumber || "",
          gender: metadata?.gender || "male",
          emailVerified: u.email_confirmed_at !== null,
          accountType,
          superadmin: metadata?.superadmin || false,
        });
      }
      setIsLoading(false);
    };

    initializeAuth();

    // Listen to auth state changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (session) {
        const u = session.user;
        const metadata = u.user_metadata;
        const provider = u.app_metadata?.provider || "email";
        const accountType = provider === "google" ? "google" : provider === "apple" ? "apple" : "email";

        setUserState({
          id: u.id,
          email: u.email || "",
          name: metadata?.full_name || metadata?.name || "",
          phoneNumber: metadata?.phoneNumber || "",
          gender: metadata?.gender || "male",
          emailVerified: u.email_confirmed_at !== null,
          accountType,
          superadmin: metadata?.superadmin || false,
        });
      } else {
        setUserState(null);
      }
      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  return (
    <UserContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        setUser,
        clearUser,
        logout,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
